import { useEffect, useState } from 'react';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import {
	COUNTER_LOCALE_ATTR,
	COUNTER_MODE_ATTR,
	COUNTER_MODE_ENTRANCE,
	COUNTER_VALUE_ATTR,
} from '@worldcoo/ui/lib/counter-contract';
import type { Locale } from '@worldcoo/monolith/core';
import { DigitRoll } from './digit-roll';

/** No size of its own: cells inherit the tile's line-height, so the tile never resizes and the roll's ten cells match. */
const CELL = 'block';

/**
 * Not `AnimateNumber`, which counts through every intermediate value; this rolls only changed digits.
 * Hydration contract: both sides render the target's digits zeroed and the roll starts after mount, so
 * mismatch reporting stays on (`counter-contract.ts`).
 */
export function RollingFigure(props: {
	locale: Locale;
	value: number;
	decimalDigits: number;
	suffix?: string;
	className?: string;
}) {
	const [rolled, setRolled] = useState(false);

	// Not a layout effect: the zeroed figure must paint first, or there is nothing to transition from.
	useEffect(() => setRolled(true), []);

	const figure = formatNumber(props.locale, props.value, props.decimalDigits, { alwaysGroup: true });
	const text = props.suffix ? `${figure} ${props.suffix}` : figure;
	const shown = rolled ? text : text.replace(/\d/g, '0');

	return (
		<span
			className={props.className}
			{...{
				[COUNTER_VALUE_ATTR]: props.value,
				[COUNTER_MODE_ATTR]: COUNTER_MODE_ENTRANCE,
				[COUNTER_LOCALE_ATTR]: props.locale,
			}}
			// Page translators rewrite a figure's separators; same opt-out as `AnimateNumber`.
			translate="no"
		>
			<span className="sr-only">{text}</span>
			{/* Flex, not baseline: an `overflow-hidden` window's baseline is its bottom edge, so digits would sit low. */}
			<span aria-hidden className="inline-flex">
				{shown.split('').map((character, index) => {
					// Keyed by place from the right, or gaining a digit would roll the whole figure.
					const place = text.length - index;
					return /\d/.test(character) ? (
						// oxlint-disable-next-line react/no-array-index-key
						<DigitRoll key={place} digit={Number(character)} cell={CELL} />
					) : (
						// oxlint-disable-next-line react/no-array-index-key
						<span key={place} className={`${CELL} whitespace-pre`}>
							{character}
						</span>
					);
				})}
			</span>
		</span>
	);
}
